import { useEffect, useRef } from "react";
import { components } from "react-select";

const getOptionValue = (optionLike) => {
  if (optionLike == null) {
    return "";
  }

  if (typeof optionLike === "object") {
    return String(optionLike.value || "").trim();
  }

  return String(optionLike).trim();
};

const scrollSelectedOptionIntoView = (containerNode, selector) => {
  const selectedOption = containerNode?.querySelector(selector);
  if (!selectedOption || typeof selectedOption.scrollIntoView !== "function") {
    return;
  }

  selectedOption.scrollIntoView({ block: "center" });
};

const scheduleMenuScroll = (callback) => {
  if (
    typeof window === "undefined" ||
    typeof window.requestAnimationFrame !== "function"
  ) {
    callback();
    return () => {};
  }

  const frameId = window.requestAnimationFrame(callback);
  return () => window.cancelAnimationFrame(frameId);
};

export const AutoScrollMenuList = (props) => {
  const menuListRef = useRef(null);

  useEffect(() => {
    return scheduleMenuScroll(() => {
      scrollSelectedOptionIntoView(menuListRef.current, '[aria-selected="true"]');
    });
  }, []);

  const handleInnerRef = (node) => {
    menuListRef.current = node;

    if (typeof props.innerRef === "function") {
      props.innerRef(node);
    } else if (props.innerRef) {
      props.innerRef.current = node;
    }
  };

  return <components.MenuList {...props} innerRef={handleInnerRef} />;
};

export const autoScrollSelectComponents = {
  MenuList: AutoScrollMenuList,
};

export const prioritizeOptions = (options = [], selectedOptions = []) => {
  const rankedValues = Array.from(
    new Set(
      (Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions])
        .map(getOptionValue)
        .filter(Boolean),
    ),
  );

  if (rankedValues.length === 0) {
    return options;
  }

  const priorityMap = new Map(
    rankedValues.map((value, index) => [value, index]),
  );
  const prioritized = [];
  const remainder = [];

  options.forEach((option) => {
    const optionValue = getOptionValue(option);

    if (priorityMap.has(optionValue)) {
      prioritized.push({
        option,
        rank: priorityMap.get(optionValue),
      });
      return;
    }

    remainder.push(option);
  });

  prioritized.sort((left, right) => left.rank - right.rank);

  return prioritized.map(({ option }) => option).concat(remainder);
};

export const prioritizeSelectedOption = (options = [], selectedOption = null) =>
  prioritizeOptions(options, selectedOption ? [selectedOption] : []);

export const scrollMultiSelectToSelectionOnOpen = (containerRef) => (isOpen) => {
  if (!isOpen) {
    return;
  }

  scheduleMenuScroll(() => {
    scrollSelectedOptionIntoView(containerRef.current, ".select-item.selected");
  });
};
